import {
  DashboardPlatformFeeInput,
  FeeMode as RestFeeMode,
  FeeType as RestFeeType,
  ServiceType as RestServiceType,
} from "@/api/__generated__/schemas";
import { mapRangeEndToApi } from "@/mappers/payment.mappers";
import { FeesSegment, ServiceType } from "@/types";
import { FeeType } from "@/types/payment.types";
import { CurrencyCode } from "@/ui";

import { FEE_RANGES } from "./FeesForm.constants";
import { FeeTabData, FixedFeeData, PercentageFeeData, SubmittedFeeData } from "./FeesForm.types";

/**
 * Default per-service-type percentage-fee rates (Tournated side), expressed in
 * EUR and converted to the tab currency at prefill time via {@link convertFromEur}.
 */
export const PERCENTAGE_DEFAULTS_EUR: {
  [key in ServiceType]: { percentage: number; min: number };
} = {
  entry: { percentage: 4, min: 1 },
  license: { percentage: 10, min: 2.5 },
};

/** Default per-service-type fixed-fee amounts (Tournated side), expressed in EUR. */
export const FIXED_DEFAULTS_EUR: { [key in ServiceType]: number } = {
  entry: 1,
  license: 2.5,
};

/** Coarse EUR → currency conversion rates used only to seed sensible fee defaults. */
export const EUR_TO_CURRENCY_RATES: Partial<Record<CurrencyCode, number>> = {
  EUR: 1,
  USD: 1.18,
  GBP: 0.87,
  JPY: 182.6,
  CAD: 1.61,
  AUD: 1.67,
  CHF: 0.91,
  CNY: 8.15,
  ZAR: 18.83,
  SEK: 11.5,
  NOK: 12.8,
  DKK: 7.46,
  PLN: 4.17,
  CZK: 25.0,
  HUF: 400,
  RON: 5.0,
  BGN: 1.96,
  HRK: 7.53,
  TRY: 40.0,
  INR: 102,
  BRL: 6.7,
  RUB: 108,
  KRW: 1620,
  MXN: 24.0,
  SGD: 1.6,
  HKD: 9.2,
  NZD: 2.05,
  THB: 41.0,
  SAR: 4.42,
  AED: 4.33,
  UAH: 49.0,
  GEL: 3.2,
  AMD: 460,
  AZN: 2.0,
  KZT: 610,
  UZS: 15100,
  RSD: 117,
  MKD: 61.5,
  BAM: 1.96,
  ALL: 98.0,
  MDL: 20.5,
  ILS: 4.3,
  IDR: 19200,
  MYR: 5.25,
  PHP: 69.0,
  PKR: 330,
  VND: 29800,
  EGP: 59.0,
  NGN: 1880,
  KES: 153,
  MAD: 11.8,
  DZD: 160,
  TND: 3.7,
  QAR: 4.3,
  KWD: 0.36,
  BHD: 0.44,
  OMR: 0.45,
  JOD: 0.84,
};

/** Convert an EUR amount to the given currency using the coarse default rates. */
export const convertFromEur = (eurAmount: number, currency: CurrencyCode): number => {
  const rate = EUR_TO_CURRENCY_RATES[currency] ?? 1;
  return Math.round(eurAmount * rate * 100) / 100;
};

export const makeDefaultTournatedPercentageFees = (
  entityType: ServiceType,
  currency: CurrencyCode
): PercentageFeeData => ({
  percentageValue: PERCENTAGE_DEFAULTS_EUR[entityType].percentage,
  minValue: convertFromEur(PERCENTAGE_DEFAULTS_EUR[entityType].min, currency),
  maxValue: null,
});

export const makeDefaultPartnerPercentageFees = (): PercentageFeeData => ({
  percentageValue: 0,
  minValue: null,
  maxValue: null,
});

export const makeDefaultTournatedFixedFees = (
  entityType: ServiceType,
  currency: CurrencyCode
): FixedFeeData => ({
  value: convertFromEur(FIXED_DEFAULTS_EUR[entityType], currency),
  minPercentage: 0,
  maxPercentage: 100,
});

export const makeDefaultPartnerFixedFees = (): FixedFeeData => ({
  value: 0,
  minPercentage: 0,
  maxPercentage: 100,
});

/** Default (percentage-mode) fee-tab values for an entity in the given currency. */
export const makeDefaultTab = (entityType: ServiceType, currency: CurrencyCode): FeeTabData => ({
  currency,
  feeType: "percentage",
  feeBasis: "perEntry",
  monetizeOnSitePayments: false,
  fixedFees: {
    tournated: FEE_RANGES.map(() => makeDefaultTournatedFixedFees(entityType, currency)),
    partner: FEE_RANGES.map(() => makeDefaultPartnerFixedFees()),
  },
  percentageFees: {
    tournated: FEE_RANGES.map(() => makeDefaultTournatedPercentageFees(entityType, currency)),
    partner: FEE_RANGES.map(() => makeDefaultPartnerPercentageFees()),
  },
});

const matchesFeeRange = (
  segmentRangeFrom: number,
  segmentRangeTo: number,
  rangeStart: number,
  rangeEnd: number
): boolean => {
  const normalizedSegmentTo = segmentRangeTo === -1 ? -1 : segmentRangeTo;
  const normalizedRangeEnd = rangeEnd === -1 ? -1 : rangeEnd;
  return segmentRangeFrom === rangeStart && normalizedSegmentTo === normalizedRangeEnd;
};

/**
 * Group a flat list of domain {@link FeesSegment} rows (for a single service type)
 * into the per-range {@link FeeTabData} the editor renders. Falls back to defaults
 * for an empty list or for ranges the segments don't cover. Entity-agnostic — the
 * organization and league prefills both feed their persisted rows through here.
 */
export const mapFeesSegmentsToFeeTabData = (
  entityType: ServiceType,
  segments: FeesSegment[],
  allowedCurrencies: CurrencyCode[]
): FeeTabData => {
  if (segments.length === 0) {
    const currency = allowedCurrencies[0] ?? "EUR";
    return makeDefaultTab(entityType, currency);
  }

  const firstSegment = segments[0];
  const feeType = firstSegment.feeType;
  const currency = firstSegment.currency;

  const fixedFees: FeeTabData["fixedFees"] = {
    tournated: FEE_RANGES.map((r) => {
      const seg = segments.find(
        (s) =>
          matchesFeeRange(s.range.from, s.range.to, r.rangeStart, r.rangeEnd) &&
          s.feeType === FeeType.FIXED &&
          s.currency === currency
      );

      if (!seg) {
        return makeDefaultTournatedFixedFees(entityType, currency);
      }

      const fixed = seg.tournated as {
        value: number | null;
        minPercentage: number | null;
        maxPercentage: number | null;
      };

      return {
        value: fixed.value,
        minPercentage: fixed.minPercentage ?? 0,
        maxPercentage: fixed.maxPercentage ?? 100,
      } as FixedFeeData;
    }),
    partner: FEE_RANGES.map((r) => {
      const seg = segments.find((s) =>
        matchesFeeRange(s.range.from, s.range.to, r.rangeStart, r.rangeEnd)
      );
      if (!seg || seg.feeType !== FeeType.FIXED) {
        return makeDefaultPartnerFixedFees();
      }
      const fixed = seg.partner as {
        value: number | null;
        minPercentage: number | null;
        maxPercentage: number | null;
      };
      return {
        value: fixed.value,
        minPercentage: fixed.minPercentage ?? 0,
        maxPercentage: fixed.maxPercentage ?? 100,
      } as FixedFeeData;
    }),
  };

  const percentageFees: FeeTabData["percentageFees"] = {
    tournated: FEE_RANGES.map((r) => {
      const seg = segments.find(
        (s) =>
          matchesFeeRange(s.range.from, s.range.to, r.rangeStart, r.rangeEnd) &&
          s.feeType === FeeType.PERCENTAGE &&
          s.currency === currency
      );

      if (!seg) {
        return makeDefaultTournatedPercentageFees(entityType, currency);
      }

      const pct = seg.tournated as {
        percentageValue: number | null;
        minValue: number | null;
        maxValue: number | null;
      };
      return {
        percentageValue: pct.percentageValue,
        minValue: pct.minValue,
        maxValue: pct.maxValue,
      } as PercentageFeeData;
    }),
    partner: FEE_RANGES.map((r) => {
      const seg = segments.find((s) =>
        matchesFeeRange(s.range.from, s.range.to, r.rangeStart, r.rangeEnd)
      );

      if (!seg || seg.feeType !== FeeType.PERCENTAGE) {
        return makeDefaultPartnerPercentageFees();
      }
      const pct = seg.partner as {
        percentageValue: number | null;
        minValue: number | null;
        maxValue: number | null;
      };
      return {
        percentageValue: pct.percentageValue,
        minValue: pct.minValue,
        maxValue: pct.maxValue,
      } as PercentageFeeData;
    }),
  };

  return {
    currency,
    feeType,
    feeBasis: firstSegment.feeBasis,
    monetizeOnSitePayments: firstSegment.enableMonetization,
    fixedFees,
    percentageFees,
  };
};

/**
 * Collapse a full {@link FeeTabData} (both fee-type row sets) down to just the
 * rows for the active `feeType`, one per {@link FEE_RANGES} range — the
 * {@link SubmittedFeeData} shape the REST row builder consumes.
 */
export const mapFeeTabToSubmittedFeeData = (feeTab: FeeTabData): SubmittedFeeData => {
  const currency = feeTab.currency;
  if (feeTab.feeType === FeeType.FIXED) {
    return {
      currency,
      feeType: FeeType.FIXED,
      feeBasis: feeTab.feeBasis,
      monetizeOnSitePayments: feeTab.monetizeOnSitePayments,
      ranges: FEE_RANGES.map((r) => ({
        rangeStart: r.rangeStart,
        rangeEnd: r.rangeEnd === -1 ? null : r.rangeEnd,
        tournated: [feeTab.fixedFees.tournated[r.id]],
        partner: [feeTab.fixedFees.partner[r.id]],
      })),
    };
  }
  return {
    currency,
    feeType: FeeType.PERCENTAGE,
    feeBasis: feeTab.feeBasis,
    monetizeOnSitePayments: feeTab.monetizeOnSitePayments,
    ranges: FEE_RANGES.map((r) => ({
      rangeStart: r.rangeStart,
      rangeEnd: r.rangeEnd === -1 ? null : r.rangeEnd,
      tournated: [feeTab.percentageFees.tournated[r.id]],
      partner: [feeTab.percentageFees.partner[r.id]],
    })),
  };
};

const mapServiceTypeToRest = (serviceType: ServiceType): RestServiceType =>
  serviceType === "entry" ? RestServiceType.ENTRY : RestServiceType.LICENSE;

const mapFeeTypeToRest = (feeType: FeeType): RestFeeType =>
  feeType === FeeType.FIXED ? RestFeeType.FIXED : RestFeeType.PERCENTAGE;

const mapFeeBasisToRest = (feeBasis: string): RestFeeMode =>
  feeBasis === "perPlayer" ? RestFeeMode.PER_PLAYER : RestFeeMode.PER_ENTRY;

/**
 * The shared per-row REST builder: turn one submitted fee tab into the flat
 * {@link DashboardPlatformFeeInput}[] rows for a single service type. The
 * organization (platform-keyed) and league (flat) mappers both delegate here so
 * the fixed-vs-percentage field selection lives in one place.
 */
export const mapSubmittedFeeDataToRestPlatformFeeInputs = (
  feeData: SubmittedFeeData,
  serviceType: ServiceType
): DashboardPlatformFeeInput[] => {
  const base = {
    serviceType: mapServiceTypeToRest(serviceType),
    feeType: mapFeeTypeToRest(feeData.feeType),
    feeMode: mapFeeBasisToRest(feeData.feeBasis),
    currency: feeData.currency,
    isMonetization: feeData.monetizeOnSitePayments ?? false,
  };

  if (feeData.feeType === FeeType.FIXED) {
    return feeData.ranges.map((range) => {
      const tournated = range.tournated[0] as FixedFeeData;
      const partner = range.partner[0] as FixedFeeData;
      return {
        ...base,
        rangeStart: range.rangeStart,
        rangeEnd: mapRangeEndToApi(range.rangeEnd),
        tournatedFee: tournated.value ?? undefined,
        tournatedMinPercentageFee: tournated.minPercentage ?? undefined,
        tournatedMaxPercentageFee: tournated.maxPercentage ?? undefined,
        tournatedMinFee: undefined,
        tournatedMaxFee: undefined,
        partnerFee: partner.value ?? undefined,
        partnerMinPercentageFee: partner.minPercentage ?? undefined,
        partnerMaxPercentageFee: partner.maxPercentage ?? undefined,
        partnerMinFee: undefined,
        partnerMaxFee: undefined,
      } satisfies DashboardPlatformFeeInput;
    });
  }

  return feeData.ranges.map((range) => {
    const tournated = range.tournated[0] as PercentageFeeData;
    const partner = range.partner[0] as PercentageFeeData;
    return {
      ...base,
      rangeStart: range.rangeStart,
      rangeEnd: mapRangeEndToApi(range.rangeEnd),
      tournatedFee: tournated.percentageValue ?? undefined,
      tournatedMinFee: tournated.minValue ?? undefined,
      tournatedMaxFee: tournated.maxValue ?? undefined,
      tournatedMinPercentageFee: undefined,
      tournatedMaxPercentageFee: undefined,
      partnerFee: partner.percentageValue ?? undefined,
      partnerMinFee: partner.minValue ?? undefined,
      partnerMaxFee: partner.maxValue ?? undefined,
      partnerMinPercentageFee: undefined,
      partnerMaxPercentageFee: undefined,
    } satisfies DashboardPlatformFeeInput;
  });
};
